import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import type {
  CiRun,
  PullRequestSummary,
  FlakyTestsResponse,
  HotfixesResponse,
  ReleasesResponse,
  SyncReloadResponse,
  SyncStatusResponse,
  TicketLocation,
  VersionDetail,
} from '@internal/backstage-plugin-delivery-common';
import { apiError } from '@internal/ui';

export interface DeliveryApi {
  getPullRequests(entityRef: string): Promise<PullRequestSummary[]>;
  getCiRuns(entityRef: string): Promise<CiRun[]>;
  getReleases(): Promise<ReleasesResponse>;
  locateTicket(key: string): Promise<TicketLocation>;
  /** One version (tag): its GitHub changelog, where it went and what it shipped. */
  getVersion(version: string): Promise<VersionDetail>;
  getFlakyTests(days: number): Promise<FlakyTestsResponse>;
  /** `team`: only that team's hotfixes (`No team` for those without one). */
  getHotfixes(days: number, team?: string): Promise<HotfixesResponse>;
  /** Where each data source stands (settings page « Données »). */
  getSyncStatus(): Promise<SyncStatusResponse>;
  /** Runs the given data sources now (all when omitted); sources running or just refreshed are left alone. */
  reloadData(sources?: string[]): Promise<SyncReloadResponse>;
}

export const deliveryApiRef = createApiRef<DeliveryApi>({
  id: 'plugin.delivery.service',
});

export class DeliveryClient implements DeliveryApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async get<T>(path: string, entityRef: string): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('delivery');
    const res = await this.fetchApi.fetch(
      `${baseUrl}${path}?entityRef=${encodeURIComponent(entityRef)}`,
    );
    if (!res.ok) {
      throw await apiError(res);
    }
    return (await res.json()).items;
  }

  getPullRequests(entityRef: string) {
    return this.get<PullRequestSummary[]>('/entity/pull-requests', entityRef);
  }

  getCiRuns(entityRef: string) {
    return this.get<CiRun[]>('/entity/ci', entityRef);
  }

  private async getJson<T>(path: string): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('delivery');
    const res = await this.fetchApi.fetch(`${baseUrl}${path}`);
    if (!res.ok) {
      throw await apiError(res);
    }
    return res.json();
  }

  getReleases() {
    return this.getJson<ReleasesResponse>('/releases');
  }

  locateTicket(key: string) {
    return this.getJson<TicketLocation>(
      `/releases/ticket/${encodeURIComponent(key.trim())}`,
    );
  }

  getVersion(version: string) {
    return this.getJson<VersionDetail>(
      `/releases/versions/${encodeURIComponent(version)}`,
    );
  }

  getFlakyTests(days: number) {
    return this.getJson<FlakyTestsResponse>(`/flaky-tests?days=${days}`);
  }

  getHotfixes(days: number, team?: string) {
    const params = new URLSearchParams({ days: String(days) });
    if (team) params.set('team', team);
    return this.getJson<HotfixesResponse>(`/hotfixes?${params}`);
  }

  getSyncStatus() {
    return this.getJson<SyncStatusResponse>('/sync');
  }

  async reloadData(sources?: string[]) {
    const baseUrl = await this.discoveryApi.getBaseUrl('delivery');
    const res = await this.fetchApi.fetch(`${baseUrl}/sync/reload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sources ? { sources } : {}),
    });
    if (!res.ok) {
      throw await apiError(res);
    }
    return (await res.json()) as SyncReloadResponse;
  }
}
